import { frameBox, selectionPad, type TextMeasure } from './markupBounds.ts';
import { boxContains, inflateBox } from './markupConstraints.ts';
import {
    constrainStroke,
    draftShape,
    extendStroke,
    finishStroke,
    type StrokeDraft
} from './markupDrafts.ts';
import { distance, type Size } from './markupGeometry.ts';
import type { MarkupAction } from './markupHistory.ts';
import { topmostHit, type HitOptions } from './markupHitTest.ts';
import type { Box, MarkupShape, MarkupTool, Point } from './markupShapes.ts';
import {
    clampMove,
    handleAt,
    resizeShape,
    shapeHandles,
    translateShape,
    type Handle,
    type HandleId
} from './markupTransforms.ts';

export type Gesture =
    | { type: 'draw'; draft: StrokeDraft }
    | { type: 'move'; original: MarkupShape; frame: Box; image: Size; start: Point; current: Point }
    | {
          type: 'resize';
          original: MarkupShape;
          frame: Box;
          handle: HandleId;
          start: Point;
          current: Point;
          // Press point minus the handle's true position, so grabbing off-centre doesn't jump.
          grab: Point;
          constrain: boolean;
      };

export type SelectTarget =
    | { type: 'handle'; shape: MarkupShape; handle: Handle }
    | { type: 'shape'; shape: MarkupShape }
    | { type: 'none' };

// Handles are a little easier to grab than strokes; below MIN_DRAG a press is a click.
const HANDLE_REACH_RATIO = 1.5;
const MIN_DRAG_SCREEN_PX = 2;

const HANDLE_CURSORS: Record<HandleId, string> = {
    nw: 'nwse-resize',
    se: 'nwse-resize',
    ne: 'nesw-resize',
    sw: 'nesw-resize',
    n: 'ns-resize',
    s: 'ns-resize',
    e: 'ew-resize',
    w: 'ew-resize',
    from: 'crosshair',
    to: 'crosshair'
};

export function selectTargetAt(
    shapes: readonly MarkupShape[],
    selected: MarkupShape | null,
    point: Point,
    options: HitOptions,
    measure: TextMeasure
): SelectTarget {
    const pad = selected ? selectionPad(selected, options.screenPx) : 0;
    if (selected) {
        const handles = shapeHandles(selected, measure, pad);
        const handle = handleAt(handles, point, options.tolerance * HANDLE_REACH_RATIO);
        if (handle) return { type: 'handle', shape: selected, handle };
    }
    const hit = topmostHit(shapes, point, options, measure);
    if (hit) return { type: 'shape', shape: hit };
    if (
        selected &&
        boxContains(inflateBox(frameBox(selected, measure), pad + options.tolerance), point)
    ) {
        return { type: 'shape', shape: selected };
    }
    return { type: 'none' };
}

export function cursorFor(tool: MarkupTool, target: SelectTarget): string {
    if (tool === 'text') return 'text';
    if (tool !== 'select') return 'crosshair';
    if (target.type === 'handle') return HANDLE_CURSORS[target.handle.id];
    return target.type === 'shape' ? 'move' : 'default';
}

export function beginDraw(draft: StrokeDraft): Gesture {
    return { type: 'draw', draft };
}

export function beginMove(
    original: MarkupShape,
    start: Point,
    image: Size,
    measure: TextMeasure
): Gesture {
    return {
        type: 'move',
        original,
        frame: frameBox(original, measure),
        image,
        start,
        current: start
    };
}

export function beginResize(
    original: MarkupShape,
    handle: HandleId,
    start: Point,
    measure: TextMeasure
): Gesture {
    const frame = frameBox(original, measure);
    const at =
        shapeHandles(original, measure).find((candidate) => candidate.id === handle)?.at ?? start;
    const grab = { x: start.x - at.x, y: start.y - at.y };
    return {
        type: 'resize',
        original,
        frame,
        handle,
        start,
        current: start,
        grab,
        constrain: false
    };
}

export function trackGesture(gesture: Gesture, points: readonly Point[], scale: number): Gesture {
    const last = points[points.length - 1];
    if (!last) return gesture;
    if (gesture.type === 'draw')
        return { ...gesture, draft: extendStroke(gesture.draft, points, scale) };
    return { ...gesture, current: last };
}

export function constrainGesture(gesture: Gesture, constrain: boolean): Gesture {
    if (gesture.type === 'draw')
        return { ...gesture, draft: constrainStroke(gesture.draft, constrain) };
    if (gesture.type === 'resize' && gesture.constrain !== constrain)
        return { ...gesture, constrain };
    return gesture;
}

export function gesturePreview(gesture: Gesture): MarkupShape {
    switch (gesture.type) {
        case 'draw':
            return draftShape(gesture.draft);
        case 'move': {
            const delta = {
                x: gesture.current.x - gesture.start.x,
                y: gesture.current.y - gesture.start.y
            };
            return translateShape(gesture.original, clampMove(gesture.frame, delta, gesture.image));
        }
        case 'resize': {
            const pointer = {
                x: gesture.current.x - gesture.grab.x,
                y: gesture.current.y - gesture.grab.y
            };
            return resizeShape(
                gesture.original,
                gesture.frame,
                gesture.handle,
                pointer,
                gesture.constrain
            );
        }
    }
}

export function finishGesture(gesture: Gesture, scale: number): MarkupAction | null {
    if (gesture.type === 'draw') {
        const shape = finishStroke(gesture.draft, scale);
        return shape ? { type: 'add', shape } : null;
    }
    if (distance(gesture.start, gesture.current) * scale < MIN_DRAG_SCREEN_PX) return null;
    return { type: 'update', shape: gesturePreview(gesture) };
}
